import { useEffect, useRef, useState } from "react";
import { Platform, Share, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import * as Clipboard from "expo-clipboard";
import { useCSSVariable } from "uniwind";
import { IconButton } from "@/shared/components/ui/icon-button";
import { posthog } from "@/shared/lib/posthog";

type Feedback = "copied" | "error" | null;

interface ShareProfileButtonProps {
  userId: string;
  displayName: string;
}

export function ShareProfileButton({
  userId,
  displayName,
}: ShareProfileButtonProps) {
  const foreground = useCSSVariable("--color-foreground") as string;
  const [feedback, setFeedback] = useState<Feedback>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const showFeedback = (value: Feedback) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setFeedback(value);
    timerRef.current = setTimeout(() => setFeedback(null), 2000);
  };

  const handleShare = async () => {
    posthog?.capture("profile_shared", {
      profile_user_id: userId,
      share_method: Platform.OS === "web" ? "clipboard" : "native_share",
    });

    if (Platform.OS === "web") {
      const url = `${window.location.origin}/user/${userId}`;
      try {
        await Clipboard.setStringAsync(url);
        showFeedback("copied");
      } catch {
        showFeedback("error");
      }
      return;
    }

    const webUrl = `${process.env.EXPO_PUBLIC_DOMAIN_URL ?? "https://app.lezz.social"}/user/${userId}`;
    const message = `Confira o perfil de ${displayName} no Lezz`;
    const title = `${displayName} no Lezz`;
    // Make sharing message platform-agnostic
    await Share.share(
      Platform.OS === "ios"
        ? { title, message, url: webUrl }
        : { title, message: `${message} ${webUrl}` }
    );
  };

  return (
    <View className="relative">
      <IconButton
        size="sm"
        shape="circle"
        surface="tinted"
        tone="neutral"
        onPress={handleShare}
        accessibilityLabel="Compartilhar perfil"
      >
        <Ionicons name="share-social-outline" size={20} color={foreground} />
      </IconButton>
      {feedback && (
        <View
          className="bg-foreground absolute top-full right-0 mt-1 items-center rounded px-2 py-1"
          style={{ width: 140, zIndex: 999 }}
        >
          <Text
            className="text-background text-xs font-medium"
            numberOfLines={1}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
          >
            {feedback === "copied" ? "Copiado" : "Não foi possível copiar"}
          </Text>
        </View>
      )}
    </View>
  );
}
